// vim: set ts=2 sts=2 sw=2 et:
//
// Pure helper for merging one platform's entries (from another PC's save state) into the local
// meet, re-keying entry ids AND their WRPF anchors so merged entries keep their `userOnTournamentId`
// when results are published. Used by both the file-based merge (ResultsView) and the web sync.

import type { Entry } from "../../types/dataTypes";
import type { WrpfEntryAnchor } from "./types";
import { normalizeEntryDivisions } from "./normalizeWrpfDivisions";

export type PlatformMergePayload = {
  day: number;
  platform: number;
  /** Incoming entries, already re-keyed to fresh local ids. */
  platformEntries: Entry[];
  /** Full new anchor list for `wrpfSession.entryAnchors` (old (day,platform) anchors replaced). */
  anchors: WrpfEntryAnchor[];
  /** New `registration.nextEntryId` after assigning ids to the merged entries. */
  nextEntryId: number;
};

/**
 * Re-keys the incoming (day, platform) entries to fresh local ids and carries their anchors across,
 * dropping the anchors of the local entries being replaced. Single source of truth for the id
 * assignment so the registration slice (entries) and the wrpfSession slice (anchors) stay consistent.
 */
export function buildPlatformMerge(params: {
  currentEntries: ReadonlyArray<Entry>;
  currentAnchors: ReadonlyArray<WrpfEntryAnchor>;
  nextEntryId: number;
  day: number;
  platform: number;
  incomingEntries: ReadonlyArray<Entry>;
  incomingAnchors: ReadonlyArray<WrpfEntryAnchor>;
  /**
   * Normalizar divisiones al reglamento WRPF Argentina (bandas Master de 10 años). Activarlo cuando
   * hay sesión WRPF activa: la otra PC pudo haber cargado un bundle viejo con bandas de 4-5 años y
   * re-inyectarlas acá mezclaría categorías en Resultados.
   */
  normalizeDivisions?: boolean;
}): PlatformMergePayload {
  const { day, platform } = params;

  const foreignForPlatform = params.incomingEntries.filter((e) => e.day === day && e.platform === platform);

  let nextEntryId = params.nextEntryId;
  const platformEntries: Entry[] = [];
  const rekeyedAnchors: WrpfEntryAnchor[] = [];

  for (const foreign of foreignForPlatform) {
    const newId = nextEntryId++;
    const rekeyed = { ...foreign, id: newId };
    platformEntries.push(params.normalizeDivisions ? normalizeEntryDivisions(rekeyed) : rekeyed);

    const foreignAnchor = params.incomingAnchors.find((a) => a.openlifterEntryId === foreign.id);
    if (foreignAnchor) {
      rekeyedAnchors.push({ ...foreignAnchor, openlifterEntryId: newId });
    }
  }

  // Drop anchors for the local entries on this (day, platform) that we just replaced.
  const removedEntryIds = new Set(
    params.currentEntries.filter((e) => e.day === day && e.platform === platform).map((e) => e.id),
  );
  const anchors = params.currentAnchors.filter((a) => !removedEntryIds.has(a.openlifterEntryId)).concat(rekeyedAnchors);

  return { day, platform, platformEntries, anchors, nextEntryId };
}
